import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { StoreStatus } from 'common/utils/types'
import { type ValidationMessage } from 'utils/types'

import {
  editVendor,
  editVendorIndustries,
  editVendorSpecialties,
  getDecisionDetailsCompany,
  getMyOffers,
  getVendorCompany,
  getVendorInfo,
  initializeVendor,
  respondToVerification,
  sendAIVerificationRequest,
  setAdditionalDocuments,
  setAIRefDocuments,
  setRefDocuments,
} from './vendor.service'
import {
  type DecisionDetails,
  type MyOffer,
  type Vendor,
  type VendorCompany,
  type VendorStatus,
} from './vendor.types'
import { getVendorStatus } from './vendor.functions'

export interface VendorState {
  status: StoreStatus
  vendor: Vendor | undefined
  vendorInitializationStatus: StoreStatus
  editVendorStatus: StoreStatus
  validationErrors: ValidationMessage[]
  refDocumentsStatus: StoreStatus
  aiReferenceDocumentsStatus: StoreStatus
  companyVendorStatus: StoreStatus
  companyVendor: VendorCompany | null
  respondVerificationStatus: StoreStatus
  decisionDetails: DecisionDetails | null
  decisionDetailsStatus: StoreStatus
  errorCode: string
  additionalDocumentsStatus: StoreStatus
  vendorSpecialtiesStatus: StoreStatus
  vendorIndustriesStatus: StoreStatus
  myOffersList: MyOffer[]
  myOffersCount: number
  myOffersPage: number
  getMyOffersStatus: StoreStatus
  sendAIVerificationRequestStatus: StoreStatus
}

const initialState: VendorState = {
  status: StoreStatus.Idle,
  vendor: undefined,
  vendorInitializationStatus: StoreStatus.Idle,
  editVendorStatus: StoreStatus.Idle,
  validationErrors: [],
  refDocumentsStatus: StoreStatus.Idle,
  aiReferenceDocumentsStatus: StoreStatus.Idle,
  companyVendorStatus: StoreStatus.Idle,
  companyVendor: null,
  respondVerificationStatus: StoreStatus.Idle,
  decisionDetails: null,
  decisionDetailsStatus: StoreStatus.Idle,
  errorCode: '',
  additionalDocumentsStatus: StoreStatus.Idle,
  vendorSpecialtiesStatus: StoreStatus.Idle,
  vendorIndustriesStatus: StoreStatus.Idle,
  myOffersList: [],
  myOffersCount: 0,
  myOffersPage: 0,
  getMyOffersStatus: StoreStatus.Idle,
  sendAIVerificationRequestStatus: StoreStatus.Idle,
}

export const VendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    resetState: (state) => ({
      ...initialState,
    }),
    changeVendorStatus: (state, payload: { payload: VendorStatus }) => ({
      ...state,
      vendor: state.vendor
        ? {
            ...state.vendor,
            status: payload.payload,
          }
        : undefined,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(initializeVendor.pending, (state, { payload }) => ({
      ...state,
      vendorInitializationStatus: StoreStatus.Loading,
    }))
    builder.addCase(initializeVendor.fulfilled, (state, { payload }) => ({
      ...state,
      vendor: payload,
      vendorInitializationStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(initializeVendor.rejected, (state, { error }) => ({
      ...state,
      vendor: undefined,
      vendorInitializationStatus: StoreStatus.Failed,
      errorCode: error.code ?? '',
    }))
    builder.addCase(getVendorInfo.pending, (state, { payload }) => ({
      ...state,
    }))
    builder.addCase(getVendorInfo.fulfilled, (state, { payload }) => ({
      ...state,
      vendor: payload,
    }))
    builder.addCase(getVendorInfo.rejected, (state, { error }) => ({
      ...state,
      vendor: undefined,
      errorCode: error.code ?? '',
    }))
    builder.addCase(editVendor.pending, (state) => ({
      ...state,
      editVendorStatus: StoreStatus.Loading,
    }))
    builder.addCase(editVendor.fulfilled, (state, { payload }) => {
      let vendorContact = {}

      if (payload.contactPerson) {
        vendorContact = {
          contact: {
            name: {
              first: payload.contactPerson.firstName,
              last: payload.contactPerson.lastName,
            },
            role: payload.contactPerson.role,
            phone: payload.contactPerson.phone,
            email: payload.contactPerson.email,
          },
        }
        delete payload.contactPerson
      }

      return {
        ...state,
        ...(state.vendor && { vendor: { ...state.vendor, ...payload, ...vendorContact } }),
        editVendorStatus: StoreStatus.Succeeded,
      }
    })
    builder.addCase(editVendor.rejected, (state) => ({
      ...state,
      editVendorStatus: StoreStatus.Failed,
    }))
    builder.addCase(setRefDocuments.pending, (state, { meta }) => {
      const documents = meta.arg.documents
      state.refDocumentsStatus = StoreStatus.Loading

      if (state.vendor && !('referenceDocuments' in state.vendor)) {
        state.vendor.referenceDocuments = { documents: [] }
      }
      if (state.vendor?.referenceDocuments) {
        state.vendor.referenceDocuments.documents = documents
      }
    })
    builder.addCase(setRefDocuments.fulfilled, (state, { payload }) => ({
      ...state,
      ...(state.vendor && { referentDocuments: payload }),
      refDocumentsStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(setRefDocuments.rejected, (state) => ({
      ...state,
      refDocumentsStatus: StoreStatus.Failed,
    }))
    builder.addCase(getVendorCompany.pending, (state) => ({
      ...state,
      companyVendorStatus: StoreStatus.Loading,
    }))
    builder.addCase(getVendorCompany.fulfilled, (state, { payload }) => ({
      ...state,
      vendor: payload,
      companyVendorStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getVendorCompany.rejected, (state) => ({
      ...state,
      companyVendorStatus: StoreStatus.Failed,
    }))
    builder.addCase(getDecisionDetailsCompany.pending, (state) => ({
      ...state,
      decisionDetailsStatus: StoreStatus.Loading,
    }))
    builder.addCase(getDecisionDetailsCompany.fulfilled, (state, { payload }) => ({
      ...state,
      decisionDetails: payload,
      decisionDetailsStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getDecisionDetailsCompany.rejected, (state) => ({
      ...state,
      decisionDetailsStatus: StoreStatus.Failed,
    }))
    builder.addCase(getMyOffers.pending, (state, { meta }) => {
      return {
        ...state,
        myOffersPage: meta.arg.page,
        getMyOffersStatus: StoreStatus.Loading,
      }
    })
    builder.addCase(getMyOffers.fulfilled, (state, { payload }) => ({
      ...state,
      myOffersList: payload.list.data,
      myOffersCount: payload.list.count,
      getMyOffersStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getMyOffers.rejected, (state) => ({
      ...state,
      myOffersList: [],
      myOffersCount: 0,
      getMyOffersStatus: StoreStatus.Failed,
    }))
    builder.addCase(respondToVerification.pending, (state) => ({
      ...state,
      respondVerificationStatus: StoreStatus.Loading,
    }))
    builder.addCase(respondToVerification.fulfilled, (state, { payload, meta }) => {
      const note = meta.arg.note
      state.respondVerificationStatus = StoreStatus.Succeeded
      if (state.vendor) {
        state.vendor.status = payload as VendorStatus
      }
      if (state.vendor && !('verificationNote' in state.vendor) && note) {
        state.vendor.verificationNote = ''
      }
      if (state.vendor && note) {
        state.vendor.verificationNote = note
      }
    })
    builder.addCase(respondToVerification.rejected, (state) => ({
      ...state,
      respondVerificationStatus: StoreStatus.Failed,
    }))
    builder.addCase(setAdditionalDocuments.fulfilled, (state, { payload }) => ({
      ...state,
      ...(state.vendor && { additionalDocuments: payload }),
      additionalDocumentsStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(setAdditionalDocuments.pending, (state, { meta }) => {
      const documents = meta.arg.documents
      state.additionalDocumentsStatus = StoreStatus.Loading

      if (state.vendor && !('additionalDocuments' in state.vendor)) {
        state.vendor.additionalDocuments = { documents: [] }
      }
      if (state.vendor?.additionalDocuments) {
        state.vendor.additionalDocuments.documents = documents
      }
    })
    builder.addCase(setAdditionalDocuments.rejected, (state) => ({
      ...state,
      additionalDocumentsStatus: StoreStatus.Failed,
    }))
    builder.addCase(editVendorSpecialties.pending, (state, { payload }) => ({
      ...state,
      vendorSpecialtiesStatus: StoreStatus.Loading,
    }))
    builder.addCase(editVendorSpecialties.fulfilled, (state, { payload }) => {
      state.vendorSpecialtiesStatus = StoreStatus.Succeeded
      if (state.vendor && !('specialties' in state.vendor)) {
        state.vendor.specialties = []
      }
      if (state.vendor) {
        state.vendor.specialties = payload.specialities
      }
    })
    builder.addCase(editVendorSpecialties.rejected, (state) => ({
      ...state,
      vendorSpecialtiesStatus: StoreStatus.Failed,
    }))
    builder.addCase(setAIRefDocuments.pending, (state, { meta }) => {
      const documents = meta.arg.documents
      state.aiReferenceDocumentsStatus = StoreStatus.Loading

      if (state.vendor && !('aiReferenceDocuments' in state.vendor)) {
        state.vendor.aiReferenceDocuments = { documents: [] }
      }
      if (state.vendor?.aiReferenceDocuments) {
        state.vendor.aiReferenceDocuments.documents = documents
      }
    })
    builder.addCase(setAIRefDocuments.fulfilled, (state, { payload }) => ({
      ...state,
      ...(state.vendor && { aiReferenceDocuments: payload }),
      aiReferenceDocuments: StoreStatus.Succeeded,
    }))
    builder.addCase(setAIRefDocuments.rejected, (state) => ({
      ...state,
      aiReferenceDocuments: StoreStatus.Failed,
    }))
    builder.addCase(editVendorIndustries.pending, (state, { payload }) => ({
      ...state,
      vendorIndustriesStatus: StoreStatus.Loading,
    }))
    builder.addCase(editVendorIndustries.fulfilled, (state, { payload }) => {
      state.vendorIndustriesStatus = StoreStatus.Succeeded
      if (state.vendor && !('industries' in state.vendor)) {
        state.vendor.industries = []
      }
      if (state.vendor) {
        state.vendor.industries = payload.industries
      }
    })
    builder.addCase(editVendorIndustries.rejected, (state) => ({
      ...state,
      vendorIndustriesStatus: StoreStatus.Failed,
    }))
    builder.addCase(sendAIVerificationRequest.pending, (state, { payload }) => ({
      ...state,
      sendAIVerificationRequestStatus: StoreStatus.Loading,
    }))
    builder.addCase(sendAIVerificationRequest.fulfilled, (state, { payload }) => ({
      ...state,
      sendAIVerificationRequestStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(sendAIVerificationRequest.rejected, (state) => ({
      ...state,
      sendAIVerificationRequestStatus: StoreStatus.Failed,
    }))
    builder.addMatcher(
      isAnyOf(
        changeVendorStatus,
        initializeVendor.fulfilled,
        editVendor.fulfilled,
        setRefDocuments.fulfilled,
        getVendorCompany.fulfilled,
        setAdditionalDocuments.fulfilled,
        respondToVerification.fulfilled,
        setAIRefDocuments.fulfilled,
        getVendorInfo.fulfilled,
      ),
      (state) => {
        const vendor = state.vendor
        if (!vendor) {
          return state
        }
        return {
          ...state,
          vendor: {
            ...vendor,
            status: getVendorStatus(vendor),
          },
        }
      },
    )
  },
})

export const { resetState, changeVendorStatus } = VendorSlice.actions

export default VendorSlice.reducer
